import React from "react";
import config from "appConfig";
import styles from "../styles/FinanceList.scss";
import DownloadButton from "./DownloadButton";
import { PaymentMethod, PaymentMethodText, ShoppingCartFormat, ShoppingCartItemType, ShoppingCartParsed, ShoppingCartStatus } from "../../../models";
import { Descriptions, Icon, Spin, Table, Tooltip } from "antd";
import { formatDate, parsePriceString } from "../../../services";
import AwaitingPaymentButton from "./AwaitingPaymentButton";
import CustomTabs from "../../../components/CustomTabs/CustomTabs";
import { FinanceListLoading } from "./FinanceListLoading";
import { TAB_INVOICES, TAB_PURCHASES } from "../constants/constants";

export interface IFinanceListProps {
  carts: ShoppingCartParsed[];
  emptyCartsText?: string;
  isMobile?: boolean;
  isLoading: boolean;
  onInvoiceDownload: (cart: ShoppingCartParsed) => void;
  onAwaitingPayment: (cart: ShoppingCartParsed) => void;
  onTabNumberChange: (key: string) => void;
  activeTab?: string;
}

const FinanceList = (props: IFinanceListProps) => {
  const {
    carts,
    isMobile,
    isLoading,
    onInvoiceDownload,
    onAwaitingPayment,
    onTabNumberChange,
  } = props;

  const activeTab = props.activeTab;

  const handleTabChange = (key: string) => {
    onTabNumberChange(key);
  };

  const columnsInvoices = [
    {
      title: "Datum izdavanja",
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => formatDate(value, config.dateFormat),
    },
    {
      title: "Naziv usluge",
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: "Broj računa",
      dataIndex: 'invoiceId',
      key: 'invoiceId',
    },
    {
      title: "Porezna osnovica",
      dataIndex: 'priceWithoutVat',
      key: 'priceWithoutVat',
      render: value => parsePriceString(value),
    },
    {
      title: "PDV",
      dataIndex: 'vat',
      key: 'vat',
      render: value => parsePriceString(value),
    },
    {
      title: "Ukupno",
      dataIndex: 'price',
      key: 'price',
      render: value => parsePriceString(value),
    },
    {
      title: "Akcije",
      key: 'actions',
      render: (text, record) => (
        record.status === ShoppingCartStatus.awaitingPayment && record.paymentMethod === PaymentMethod.bankTransfer && record.bankTransferInfo ? (
          <AwaitingPaymentButton cart={record} onAwaitingPayment={onAwaitingPayment} />
        ) : record.invoiceId ? (
          <DownloadButton cart={record} onInvoiceDownload={onInvoiceDownload} />
        ) : null
      ),
    },
  ];

  const columnsPurchases = [
    {
      title: "Vrijeme",
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => formatDate(value, config.fullTimeFormat),
    },
    {
      title: "Tip potrošnje",
      dataIndex: 'name',
      key: 'name',
      render: (value, record) => {
        return <React.Fragment>
          {record.type === ShoppingCartItemType.AutoPromotion &&
            <Tooltip title={"Automatsko reisticanje oglasa"}>
              <Icon type="sync" className={styles.icon} />
            </Tooltip>
          }
          {value}
        </React.Fragment>;
      }
    },
    {
      title: "Naslov oglasa",
      dataIndex: 'adTitle',
      key: 'adTitle',
      render: (value, record) => {
        return <a href={record.adUrl} target="_blank">{value}</a>;
      }
    },
    {
      title: "Plaćeno",
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (value, record) => {
        return <span>{PaymentMethodText.find((item) => item.value === value).name} - <span className={styles.currency}>{record.price} {value === PaymentMethod.storeCredit ? "" : "€"}</span></span>
      }
    },
  ];

  return (
    <div className={styles.content}>
      <div className={styles.actions}>
        <p className={styles.heading}>{"Na ovoj stranici prikazujemo Vam kompletnu povijest potrošnje koju ste imali na Index Oglasima."}</p>
        {!isMobile ? <p className={styles.subtitle}>{"Pregled transakcija"}</p> : null}

        <div className={styles.sectionTable}>
          <CustomTabs
            activeKey={activeTab}
            onChange={handleTabChange}
            tabs={[
              {
                key: TAB_INVOICES,
                label: "Pregled računa",
                content: isLoading ? (
                  <FinanceListLoading isMobile={isMobile} />
                ) : carts && carts.length > 0 ? (
                  !isMobile ? (
                    <Table
                      className={styles.table}
                      dataSource={carts}
                      columns={columnsInvoices}
                      pagination={false}
                      rowKey="id"
                    />
                  ) : (
                    carts.map((cart, index) => (
                      <div className={styles.tableMobile} key={index}>
                        <Descriptions layout="horizontal" bordered column={1}>
                          <Descriptions.Item label={"Datum izdavanja"}>{formatDate(cart.createdAt, config.dateFormat)}</Descriptions.Item>
                          <Descriptions.Item label={"Naziv usluge"}>{cart.name}</Descriptions.Item>
                          <Descriptions.Item label={"Broj računa"}>{cart.invoiceId}</Descriptions.Item>
                          <Descriptions.Item label={"Porezna osnovica"}>{parsePriceString(cart.priceWithoutVat)}</Descriptions.Item>
                          <Descriptions.Item label={"PDV"}>{parsePriceString(cart.vat)}</Descriptions.Item>
                          <Descriptions.Item label={"Ukupno"}>{parsePriceString(cart.price)}</Descriptions.Item>
                        </Descriptions>
                        {cart.status === ShoppingCartStatus.awaitingPayment && cart.paymentMethod === PaymentMethod.bankTransfer && cart.bankTransferInfo ? (
                          <AwaitingPaymentButton cart={cart} onAwaitingPayment={onAwaitingPayment} />
                        ) : cart.invoiceId ? (
                          <DownloadButton cart={cart} onInvoiceDownload={onInvoiceDownload} />
                        ) : null}
                      </div>
                    ))
                  )
                ) : (
                  <div className={styles.section}>
                    <p className={styles.empty}>{props.emptyCartsText}</p>
                  </div>
                ),
              },
              {
                key: TAB_PURCHASES,
                label: "Pregled potrošnje",
                content: isLoading ? (
                  <FinanceListLoading isMobile={isMobile} isPurchases />
                ) : carts && carts.length > 0 ? (
                  !isMobile ? (
                    <Table
                      className={styles.table}
                      dataSource={carts}
                      columns={columnsPurchases}
                      pagination={false}
                      rowKey="id"
                    />
                  ) : (
                    carts.map((cart, index) => (
                      <div className={styles.tableMobile} key={index}>
                        <Descriptions layout="horizontal" bordered column={1}>
                          <Descriptions.Item label={"Vrijeme"}>{formatDate(cart.createdAt, config.fullTimeFormat)}</Descriptions.Item>
                          <Descriptions.Item label={"Tip potrošnje"}>
                            {cart.type === ShoppingCartItemType.AutoPromotion &&
                              <Tooltip title={"Automatsko reisticanje oglasa"}>
                                <Icon type="sync" className={styles.icon} />
                              </Tooltip>
                            }
                            {cart.name}
                          </Descriptions.Item>
                          <Descriptions.Item label={"Naslov oglasa"}>{<a href={cart.adUrl} target="_blank">{cart.adTitle}</a>}</Descriptions.Item>
                          <Descriptions.Item label={"Plaćeno"}>
                            {PaymentMethodText.find((item) => item.value === cart.paymentMethod).name}  - <span className={styles.currency}>{cart.price} {cart.paymentMethod === PaymentMethod.storeCredit ? "" : "€"}</span>
                          </Descriptions.Item>
                        </Descriptions>
                      </div>
                    ))
                  )
                ) : (
                  <div className={styles.section}>
                    <p className={styles.empty}>{props.emptyCartsText}</p>
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

FinanceList.defaultProps = {
  carts: null,
};

export default FinanceList;



// WEBPACK FOOTER //
// ./src/containers/FinancePage/FinanceList/index.tsx